import React, { useState, useEffect, useCallback } from 'react';
import { Card, Row, Col, Typography, Spin, Statistic } from 'antd';
import { LoadingOutlined } from '@ant-design/icons';
import { ArrowUpOutlined, ArrowDownOutlined } from '@ant-design/icons';

import SnapshotService from '../services';
import { formatCurrency } from '../../../helpers/utils';

const { Title, Text } = Typography;

const DashboardStatisticsWidget = ({ startDate, endDate }) => {
  const [data, setData] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [currency_symbol, setCurrencySymbol] = useState(null);

  const getData = useCallback(() => {
    if (!startDate || !endDate) return;
    setIsLoading(true);
    SnapshotService.getDashboardStatistics({
      start_date: startDate,
      end_date: endDate
    }).then((response) => {
      try {
        if (response.data?.success) {
          const statsData = response.data.data || response.data;
          const currency = statsData.currency || response.data.currency;
          
          if (!currency || !currency.currency_symbol) {
            console.error('Currency not found in dashboard statistics response');
            setIsLoading(false);
            return;
          }
          
          setData(statsData);
          setCurrencySymbol(currency.currency_symbol);
          setIsLoading(false);
        } else {
          setIsLoading(false);
        }
      } catch (error) {
        console.error('Error fetching dashboard statistics:', error);
        setIsLoading(false);
      }
    }).catch(error => {
      console.error('Error fetching dashboard statistics:', error);
      setIsLoading(false);
    });
  }, [startDate, endDate]);

  useEffect(() => {
    if (!startDate || !endDate) {
      setIsLoading(false);
      return;
    }
    getData();
  }, [startDate, endDate, getData]);

  if (isLoading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '200px' }}>
        <Spin indicator={<LoadingOutlined spin />} />
      </div>
    );
  }

  if (!data || !currency_symbol) {
    return null;
  }

  const currentMonth = data.current_month || {};
  const lastMonth = data.last_month || {};

  // Calculate growth percentages
  const salesGrowth = lastMonth && lastMonth.total_sales_amount > 0
    ? ((currentMonth?.total_sales_amount || 0) - lastMonth.total_sales_amount) / lastMonth.total_sales_amount * 100
    : null;

  const revenueGrowth = lastMonth && lastMonth.total_sales_amount > 0
    ? ((currentMonth?.total_sales_amount || 0) - lastMonth.total_sales_amount) / lastMonth.total_sales_amount * 100
    : null;

  const customerGrowth = lastMonth && lastMonth.active_customers_count > 0
    ? ((currentMonth?.active_customers_count || 0) - lastMonth.active_customers_count) / lastMonth.active_customers_count * 100
    : null;

  return (
    <Row gutter={[16, 16]}>
      <Col xs={24} sm={12} md={6}>

        <Card>
          <Statistic
            title="Total Sales"
            value={currentMonth?.total_sales_amount || 0}
            prefix={currency_symbol}
            precision={2}
            styles={{ content: { color: '#3f8600' } }}
            suffix={
              salesGrowth !== null && (
                <span style={{ fontSize: '12px', marginLeft: '8px' }}>
                  {salesGrowth > 0 ? <ArrowUpOutlined /> : <ArrowDownOutlined />}
                  {Math.abs(salesGrowth).toFixed(1)}%
                </span>
              )
            }
          />
          <Text type="secondary" style={{ fontSize: '12px' }}>
            {currentMonth?.total_sales_count || 0} invoices
          </Text>
        </Card>
      </Col>

      <Col xs={24} sm={12} md={6}>
        <Card>
          <Statistic
            title="Payments Received"
            value={currentMonth?.total_payments_amount || 0}
            prefix={currency_symbol}
            precision={2}
            styles={{ content: { color: '#1890ff' } }}
          />
          <Text type="secondary" style={{ fontSize: '12px' }}>
            {currentMonth?.total_payments_count || 0} payments
          </Text>
        </Card>
      </Col>

      <Col xs={24} sm={12} md={6}>
        <Card>
          <Statistic
            title="Outstanding"
            value={currentMonth?.total_outstanding_amount || 0}
            prefix={currency_symbol}
            precision={2}
            styles={{ content: { color: '#cf1322' } }}
          />
          <Text type="secondary" style={{ fontSize: '12px' }}>
            {currentMonth?.overdue_sales_count || 0} overdue invoices
          </Text>
        </Card>
      </Col>

      <Col xs={24} sm={12} md={6}>
        <Card>
          <Statistic
            title="Active Customers"
            value={currentMonth?.active_customers_count || 0}
            styles={{ content: { color: '#722ed1' } }}
            suffix={
              customerGrowth !== null && (
                <span style={{ fontSize: '12px', marginLeft: '8px' }}>
                  {customerGrowth > 0 ? <ArrowUpOutlined /> : <ArrowDownOutlined />}
                  {Math.abs(customerGrowth).toFixed(1)}%
                </span>
              )
            }
          />
          <Text type="secondary" style={{ fontSize: '12px' }}>
            {currentMonth?.new_customers_count || 0} new in selected period
          </Text>
        </Card>
      </Col>
    </Row>
  );
};

export default DashboardStatisticsWidget;

